import { IoIosSearch } from "react-icons/io";
import { LuPlus } from "react-icons/lu";
import { Eye, Edit } from "lucide-react";
import { CiCircleInfo, CiEdit } from "react-icons/ci";
import { useState, useEffect, useRef } from "react";
import "./Table.css";
import { IoCloseOutline } from "react-icons/io5"; // Ensure correct icon import
import { useNavigate } from "react-router-dom";
import { CiMenuBurger } from "react-icons/ci";
import Pagination from "../Components/Pagination";
import { PiSortAscendingThin } from "react-icons/pi";
import { Snackbar, Alert } from "@mui/material";
import axiosInstance from "../axiosinstance";
import SearchableDropdown from "../Components/SearchableDropdown";

function InvoiceManagement({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]); // State to hold invoice data
  const [filteredInvoices, setFilteredInvoices] = useState([]); // State to hold filtered invoice data
  const [loading, setLoading] = useState(true); // Loading state for fetching data
  const [error, setError] = useState(null); // Error state to handle any issues during the fetch
  const [searchVisible, setSearchVisible] = useState(false); // Toggle search bar
  const [searchQuery, setSearchQuery] = useState(""); // Store search input
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isChecked, setChecked] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [selectedFirms, setSelectedFirms] = useState([]);
  const [isDeletePopup, setIsDeletePopup] = useState(false);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(null);
  const [fyLoading, setFyLoading] = useState(false);
  const [fyError, setFyError] = useState("");
  const [hasLoadedFinancialYears, setHasLoadedFinancialYears] = useState(false);
  const ITEMS_PER_PAGE = 100;

  const [page, setPage] = useState(1); // 👈 start from 1
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [totalRecords, settotalRecords] = useState();
  const containerRef = useRef();
  const debounceRef = useRef(null);
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Fetch invoices from the API
  const fetchInvoices = async (
    status = "Active",
    currentPage = page,
    perPage = itemsPerPage,
    query = ""
  ) => {
    try {
      setLoading(true);
      setError(null);
      console.log("Fetching invoices with params:", {
          query: query ? query: "", // Include search query in the API request
          page: Math.max(1, currentPage),
          per_page: perPage,
          page_status: status,
          ...(selectedFinancialYear?.start_date
            ? { fy_start: selectedFinancialYear.start_date }
            : {}),
          ...(selectedFinancialYear?.end_date
            ? { fy_end: selectedFinancialYear.end_date }
            : {}),
        });
        if(!query){
      const response = await axiosInstance.get(`/api/get_invoices/v2`, {
        params: {
          page: Math.max(1, currentPage),
          per_page: perPage,
          page_status: status,
          ...(selectedFinancialYear?.start_date
            ? { fy_start: selectedFinancialYear.start_date }
            : {}),
          ...(selectedFinancialYear?.end_date
            ? { fy_end: selectedFinancialYear.end_date }
            : {}),
        },
      });
      setInvoices(response.data.data); // Set invoices data from the response
      settotalRecords(response.data.Total_records || 0);
      setFilteredInvoices(response.data.data); // Set filtered invoices to the same initially
      setLoading(false); // Set loading to false once data is fetched
    } else {
       const response = await axiosInstance.get(`/api/search_invoice`, {
        params: {
           query: query ? query: "", // Include search query in the API request,
          page_status: "Active",
          page: Math.max(1, currentPage),
          per_page: perPage,
          ...(selectedFinancialYear?.start_date
            ? { fy_start: selectedFinancialYear.start_date }
            : {}),
          ...(selectedFinancialYear?.end_date
            ? { fy_end: selectedFinancialYear.end_date }
            : {}),
        },
      });
      const invoices = response.data.result || [];
      const totalRecordsCount = response.data.Total_records ?? invoices.length; // Ensure valid total count

      setFilteredInvoices(invoices);
      settotalRecords(totalRecordsCount);
      setLoading(false);
    }
    } catch (error) {
      setError("Failed to fetch invoices");
      setLoading(false);
    }
  };

  const fetchFinancialYears = async () => {
    setFyLoading(true);
    setFyError("");

    try {
      const response = await axiosInstance.get(`/api/financial-years`);
      const list = Array.isArray(response?.data?.data) ? response.data.data : [];
      const defaultFinancialYear =
        list.find((financialYear) => financialYear.is_active) || list[0] || null;

      setFinancialYears(list);
      setSelectedFinancialYear(defaultFinancialYear);
    } catch (fetchError) {
      console.error("Error fetching financial years:", fetchError);
      setFyError("Failed to fetch financial years.");
      setFinancialYears([]);
      setSelectedFinancialYear(null);
    } finally {
      setFyLoading(false);
      setHasLoadedFinancialYears(true);
    }
  };

  const handleSort = (key) => {
    // If the same column is clicked again, toggle the direction.
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sortedInvoices = [...filteredInvoices].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];

      // Special handling for date fields
      if (key === "due_date" || key === "invoice_date") {
        aValue = new Date(a[key]);
        bValue = new Date(b[key]);
      }

      // Special handling for numerical fields (e.g., total_amount)
      if (key === "total_amount") {
        aValue = parseFloat(a[key]);
        bValue = parseFloat(b[key]);
      }

      // Compare the values and return the sort order.
      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    // Update the sort configuration and set the sorted invoices
    setSortConfig({ key, direction });
    setFilteredInvoices(sortedInvoices); // Set sorted invoices
  };

  useEffect(() => {
    fetchFinancialYears();
  }, []);

  useEffect(() => {
    if (!hasLoadedFinancialYears) return;
    fetchInvoices("Active", page, itemsPerPage, searchQuery);
  }, [hasLoadedFinancialYears, selectedFinancialYear, page, itemsPerPage]);

  // Handle search query change
  // const handleSearch = async (e, newPage = 1, per_page = ITEMS_PER_PAGE) => {
  //   const query = e.target.value;
  //   setSearchQuery(query); // Update search query state
  //   if (newPage !== page) {
  //     setPage(newPage);
  //   }
  //   if (per_page !== ITEMS_PER_PAGE) {
  //     setItemsPerPage(per_page);
  //   }

  //   if (query.trim() === "") {
  //     // If search query is empty, fetch all data
  //     fetchInvoices("Active", newPage, per_page);
  //   } else {
  //     try {
  //       setLoading(true); // Start loading
  //       const authKey = localStorage.getItem("authKey");

  //       const response = await axios.get(`${API_URL}/api/search_invoice`, {
  //         params: { query, page_status: "Active", page: newPage, per_page },
  //         headers: {
  //           Authorization: `Bearer ${authKey}`,
  //         },
  //       });
  //       const totalRecordsCount = response.data.data;

  //       setFilteredInvoices(response.data.result || []); // Update state with filtered data
  //       settotalRecords(totalRecordsCount);
  //       setLoading(false); // Stop loading
  //     } catch (error) {
  //       console.error("Error fetching search results:", error.message);
  //       setLoading(false); // Stop loading on error
  //     }
  //   }
  // };
const performSearch = async (query, newPage, per_page) => {
  if (!query.trim()) {
    return fetchInvoices("Active", newPage, per_page, "");
  }

  try {
    setLoading(true);

    const response = await axiosInstance.get(`/api/search_invoice`, {
      params: {
        query,
        page_status: "Active",
        page: newPage,
        per_page,
        ...(selectedFinancialYear?.start_date && {
          fy_start: selectedFinancialYear.start_date,
        }),
        ...(selectedFinancialYear?.end_date && {
          fy_end: selectedFinancialYear.end_date,
        }),
      },
    });

    setFilteredInvoices(response.data.result || []);
    settotalRecords(response.data.Total_records ?? 0);
  } catch (error) {
    console.error("Error fetching search results:", error.message);
    setFilteredInvoices([]);
    settotalRecords(0);
  } finally {
    setLoading(false);
  }
};
  

const handleSearch = (e, newPage = 1, per_page = ITEMS_PER_PAGE) => {
  const query = e.target.value;
  setSearchQuery(query);
  console.log("Search Query:", query);
  if(!query.trim()){

    setPage(1);
    setItemsPerPage(ITEMS_PER_PAGE);
    return fetchInvoices("Active", 1, ITEMS_PER_PAGE, "");
  }
  if (debounceRef.current) {
    clearTimeout(debounceRef.current);
  }

  debounceRef.current = setTimeout(() => {
    performSearch(query, newPage, per_page);
  }, 400); // ⏳ delay
};

  const handleInvoiceForm = () => {
    navigate("/Invoice-Form"); // Navigate to the invoice form
  };

  const handleFinancialYearChange = (option) => {
    setSelectedFinancialYear(option || null);
  };

  // This function is called when clicking on the invoice number
  const Viewhistory = (invoice_id) => {
    navigate(`/Invoice-log/${invoice_id}`); // Navigate to the history page for the clicked invoice
  };

  function formatDate(dateString) {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
    };

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", options);
  }

  const handleEdit = (invoice_id) => {
    navigate(`/edit-invoice/${invoice_id}`);
  };

  const handleView = (id) => {
    navigate(`/View-Invoice/${id}`);
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    if (newSelectAll) {
      const allInvoiceIds = invoices.map((invoice) => invoice.invoice_id);
      console.log("Selecting all invoices:", allInvoiceIds);
      setSelectedRows(allInvoiceIds);
    } else {
      console.log("Deselecting all invoices.");
      setSelectedRows([]);
    }

    setTimeout(() => setChecked(newSelectAll), 0);
  };
  const handleCheckboxChange = (invoiceId) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(invoiceId)
        ? prevSelected.filter((id) => id !== invoiceId)
        : [...prevSelected, invoiceId]
    );
  };
  useEffect(() => {
    const allSelected =
      invoices.length > 0 &&
      invoices.every((invoice) => selectedRows.includes(invoice.invoice_id));
    setSelectAll(allSelected);
  }, [selectedRows, invoices]);

  const handleCancel = () => {
    setIsDeletePopup(false);
  };

  const handleArchiveInvoice = async () => {
    try {
      const response = await axiosInstance.put(`/api/archive_invoice`, {
        ids: selectedRows,
      });
      if (response.status === 200) {
        // Set manual success message
        setSnackbarMessage("Invoice/Invoices archived Successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        await fetchInvoices("Active", page);
        setSelectedRows([]);
        setSelectAll(false);
      }
    } catch (error) {
      // Keep the exact error message from backend
      setSnackbarMessage(
        error.response?.data?.Message || error.response?.data?.message
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsDeletePopup(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsSortPopup(false);
      }
    };

    // Only add the event listener when the popup is open
    if (isSortPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSortPopup]);

  const handleSortpopup = (event) => {
    event.stopPropagation(); // Prevent event bubbling
    setIsSortPopup(!isSortPopup);
  };

  const handleArchivedTable = () => {
    navigate("/archived-invoice");
  };

  const handlePaginationChange = (newPage, itemsPerPage) => {
    console.log("Pagination Change - Current Page:", newPage);
    setPage(newPage);
    setItemsPerPage(itemsPerPage);
  };

  // function formatNumbers(number) {
  //   const parts = number.toString().split("."); // Split into integer and decimal parts
  //   const integerPart = parts[0]; // The part before the decimal
  //   const decimalPart = parts.length > 1 ? "." + parts[1] : ""; // The part after the decimal

  //   let formattedIntegerPart = "";
  //   let count = 0;

  //   for (let i = integerPart.length - 1; i >= 0; i--) {
  //     formattedIntegerPart = integerPart[i] + formattedIntegerPart;
  //     count++;

  //     if (
  //       (count === 3 && i !== 0) || // Add comma after the first 3 digits
  //       (count > 3 && (count - 3) % 2 === 0 && i !== 0) // Add comma every 2 digits thereafter
  //     ) {
  //       formattedIntegerPart = "," + formattedIntegerPart;
  //     }
  //   }

  //   return formattedIntegerPart + decimalPart;
  // }
  function formatNumbers(number) {
    if (number === null || number === undefined || isNaN(number)) return "N/A";

    return new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(number);
  }

  return (
    <div
      className={`table-container invoice-management-page ${isSidebarExpanded ? "expanded" : "collapsed"
        }`}
    >
      <div className="table-container-content">
        <div className="table-header">
          {selectedRows.length > 0 ? ( // Change from selectedFirms to selectedRows
            <div className="table-header-content archive-header">
              <button className="delete" onClick={() => setIsDeletePopup(true)}>
                Move to Archive
              </button>
              <button
                className="action-header-close-button"
                onClick={() => setSelectedRows([])} // Deselect all checkboxes
              >
                <IoCloseOutline className="action-header-close-icon" />
              </button>
            </div>
          ) : (
            <div className="table-header-content">
              <div className="table-left-side">
                <h2 className="jost-bold">Invoice Management</h2>
              </div>
              <div className="table-right-side">
                <div className="ledger-filters-row">
                  <div className="fy-filter-container">
                    <span className="fy-filter-title">Financial Year</span>
                    <SearchableDropdown
                      value={selectedFinancialYear?.id ?? ""}
                      options={financialYears}
                      getOptionValue={(financialYear) => financialYear.id}
                      getOptionLabel={(financialYear) => financialYear.fy_label}
                      placeholder="Select Financial Year"
                      searchPlaceholder="Search financial year"
                      onChange={handleFinancialYearChange}
                      disabled={
                        fyLoading || !!fyError || financialYears.length === 0
                      }
                    />
                  </div>
                  <div className="search-bar-container">
                    <input
                      type="text"
                      placeholder="Search ..."
                      className="search-bar"
                      value={searchQuery}
                      onChange={handleSearch} // Use handleSearch to update search query and fetch data
                    />
                    <IoIosSearch className="search-bar-icon" />
                  </div>
                </div>
                <div className="new-button" onClick={handleInvoiceForm}>
                  <span className="new-icon">
                    <LuPlus />
                  </span>
                  <span>New</span>
                </div>
                <button className="invoice-sort-button" onClick={handleSortpopup}>
                  <span className="action-icon custom-action-icon-size">
                    <PiSortAscendingThin />
                  </span>
                </button>

                {isSortPopup && (
                  <div className="sort-popup-container" ref={containerRef}>
                    <div className="sort-popup-header">
                      <span className="sort-title">Sort By</span>
                    </div>
                    <div className="sort-popup-content">
                      <button
                        className="sort-button sort-button-options"
                        onClick={() => handleSort("client_name")}
                      >
                        Client Name
                      </button>
                      <button
                        className="sort-button sort-button-options"
                        onClick={() => handleSort("due_date")}
                      >
                        Due Date
                      </button>
                      <button
                        className="sort-button sort-button-options"
                        onClick={() => handleSort("total_amount")}
                      >
                        Total Amount
                      </button>
                      <button
                        className="sort-button sort-button-options"
                        onClick={() => handleSort("invoice_date")}
                      >
                        Date
                      </button>
                    </div>
                    <button
                      className="archive-button"
                      onClick={handleArchivedTable}
                    >
                      Archived Invoice
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {isDeletePopup && (
          <div className="confirm-delete-popup">
            <div className="confirm-delete-popup-content">
              <div className="confirm-model">
                <div className="confirm-model-content">
                  <div className="confirm-model-content-body">
                    <p className="archived-warning">
                      Once archived, this data cannot be restored. Please
                      proceed with caution.
                    </p>
                  </div>
                  <div className="confirm-model-content-footer">
                    <button
                      className="confirm-delete-button"
                      onClick={handleArchiveInvoice}
                    >
                      Yes
                    </button>
                    <button
                      className="confirm-cancel-button"
                      onClick={handleCancel}
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="data-list-body dlb-h58">
          <div className="data-list-scroll-body dl-sb-h58">
            {/* <div className="data-list-table"> */}
              <table className="table multilevel-data df-txt-left">
                <colgroup>
                  <col className="invoice-col-select" />
                  <col className="invoice-col-sr" />
                  <col className="invoice-col-date" />
                  <col className="invoice-col-invoice" />
                  <col className="invoice-col-customer" />
                  <col className="invoice-col-company" />
                  <col className="invoice-col-status" />
                  <col className="invoice-col-due" />
                  <col className="invoice-col-amount" />
                  <col className="invoice-col-action" />
                </colgroup>
                <thead className="sticky-header">
                  <tr className="table-row">
                    <th>
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Sr. No</th>
                    <th>Date</th>
                    <th>Invoice</th>
                    <th>
                      <span style={{
                        textAlign: 'left',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center'
                      }}>Customer Name</span>
                    </th>
                    <th>
                      <span style={{
                        textAlign: 'left',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center'
                      }}>Client Company Name</span>
                    </th>
                    <th>Status</th>
                    <th>Due Date</th>
                    <th className="price-left-align"> <span style={{ textAlign: 'center' }}>Total Amount</span></th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="center-align">
                        Loading invoices...
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="10" className="center-align">
                        {error}
                      </td>
                    </tr>
                  ) : filteredInvoices?.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="no-data-text">
                        Currently, there are no records to display
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map((invoice) => (
                      <tr key={invoice.invoice_id} className="hoverable-row">
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(invoice.invoice_id)}
                            onChange={() =>
                              handleCheckboxChange(invoice.invoice_id)
                            }
                          />
                        </td>
                        <td className="center-align">
                          {invoice.serial_number}
                        </td>
                        <td className="center-align">
                          {formatDate(invoice.invoice_date)}
                        </td>
                        <td
                          className="center-align clickable"
                          onClick={() => Viewhistory(invoice.invoice_id)}
                        >
                          {invoice.invoice_number || "N/A"}
                        </td>
                        <td>
                          {invoice.client_name
                            ? invoice.client_name
                              .toLowerCase()
                              .split(' ')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')
                            : "N/A"}
                        </td>
                        <td>
                          {invoice.client_company_name
                            ? invoice.client_company_name
                              .toLowerCase()
                              .split(' ')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')
                            : "N/A"}
                        </td>
                        <td>
                          <div
                            className={`invoice-status ${invoice.status === "paid"
                                ? "status-paid"
                                : invoice.status === "unpaid"
                                  ? "status-unpaid"
                                  : "status-partial"
                              }`}
                          >
                            {invoice.status}
                          </div>
                        </td>

                        <td className="center-align">
                          {formatDate(invoice.due_date)}
                        </td>
                        <td className="center-align price-left-align">
                          {formatNumbers(invoice.total_amount ?? "N/A")}
                        </td>
                        <td className="center-align">
                          <div className="action-btn">
                            <span
                              className="action-icon"
                              onClick={() => handleView(invoice.invoice_id)}
                            >
                              <Eye size={18} color="gray" />
                            </span>
                            <span
                              className={`action-icon ${invoice.status === "paid" ? "disabled-icon" : ""
                                }`}
                              onClick={() =>
                                invoice.status !== "paid" &&
                                handleEdit(invoice.invoice_id)
                              }
                            >
                              <CiEdit size={18} color="gray" />
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            {/* </div> */}
          </div>
          <Pagination
            className="invoice-pagination"
            showPerPage={ITEMS_PER_PAGE}
            total={totalRecords}
            onPaginationChange={handlePaginationChange}
          />
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            "& .MuiAlert-icon": {
              color: snackbarSeverity === "error" ? "#ef5350" : "#4caf50",
            },
            "& .MuiAlert-message": {
              color: snackbarSeverity === "error" ? "#ef5350" : "#4caf50",
              fontWeight: 500,
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default InvoiceManagement;
